import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { mdiPencil, mdiPlus, mdiTrashCanOutline } from '@mdi/js'
import { apiClient } from '../../api/client'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Alert, AlertDescription } from '../ui/alert'
import { AppIcon } from '../ui/icon'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { EmptyState } from '../ui/empty-state'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { DeleteConfirmDialog } from '../ui/delete-confirm-dialog'
import { useCan } from '../../contexts/PermissionsContext'
import { useFormatters } from '../../lib/format'
import { useDeleteRentTerm, useRentTerms, type LeaseRentTerm } from '../../api/leaseRent'
import { RentTermDialog, type TermDraft } from './RentTermDialog'
import { ReviewTermsCard } from './ReviewTermsCard'
import { RentReviewsSection } from './RentReviewsSection'

interface ContractAssetRow {
  uid: string
  asset_uid: string
  is_primary?: boolean
  asset?: { name?: string }
}

/** The « Échéancier du loyer » tab of a lease: the anchor lot, the dated rent rows, the review formula. */
export function RentScheduleTab({ org, contractUid, assets }: { org: string; contractUid: string; assets: ContractAssetRow[] }) {
  const { t } = useTranslation('contracts')
  const { formatDate, formatMoney } = useFormatters()
  const canWrite = useCan('contracts.write')
  const queryClient = useQueryClient()
  const { data, isLoading } = useRentTerms(org, contractUid)
  const remove = useDeleteRentTerm()
  const [dialog, setDialog] = useState<{ term: LeaseRentTerm | null; draft?: TermDraft } | null>(null)

  const terms = data?.terms ?? []
  const prefill = data?.prefill

  const choosePrimary = async (assetUid: string) => {
    await apiClient.PUT('/api/v1/organizations/{orgCode}/contracts/{contractUid}/assets/{assetUid}/primary', {
      params: { path: { orgCode: org, contractUid, assetUid } },
    })
    await queryClient.invalidateQueries({
      predicate: (q) => {
        const path = (q.queryKey as [string, string])[1]
        return typeof path === 'string' && path.includes('/contracts')
      },
    })
  }

  const dueSummary = (term: LeaseRentTerm) => {
    const r = term.due_rule
    if (!r) return null
    const base = `${t(`lease.rent_schedule.terms.${r.term}` as any)}, ${t(`lease.rent_schedule.anchors.${r.period_anchor}` as any)}`
    return r.due_day ? `${base}, ${t('lease.rent_schedule.day_n', { n: r.due_day })}` : base
  }

  const confirmInitial = () =>
    setDialog({
      term: null,
      draft: {
        effective_from: prefill?.effective_from ?? '',
        rent_minor: prefill?.rent_minor,
        currency: prefill?.currency ?? data?.currency_proposal,
        charges_minor: prefill?.charges_minor,
        charges_mode: prefill?.charges_minor ? 'provision' : 'none',
        frequency: prefill?.frequency ?? 'monthly',
        rent_free_from: prefill?.rent_free_from,
        rent_free_to: prefill?.rent_free_to,
        term: prefill?.due_rule?.term ?? prefill?.term,
        period_anchor: prefill?.due_rule?.period_anchor,
        due_day: prefill?.due_rule?.due_day,
      },
    })

  return (
    <div className="space-y-6" data-testid="rent-schedule-tab">
      {assets.length > 1 && (
        <Card>
          <CardHeader><CardTitle className="text-base">{t('lease.rent_schedule.anchor_title')}</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {!assets.some((a) => a.is_primary) && (
              <Alert data-testid="rent-anchor-missing-banner">
                <AlertDescription>{t('contract_primary.missing')}</AlertDescription>
              </Alert>
            )}
            {assets.map((a) => (
              <label key={a.uid} className="flex items-center gap-2 text-sm">
                <input type="radio" name={`rent-anchor-${contractUid}`} checked={!!a.is_primary} disabled={!canWrite}
                  onChange={() => void choosePrimary(a.asset_uid)} data-testid={`rent-anchor-radio-${a.asset_uid}`} />
                {a.asset?.name ?? a.asset_uid}
              </label>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">{t('lease.rent_schedule.title')}</CardTitle>
          {canWrite && terms.length > 0 && (
            <Button size="sm" onClick={() => setDialog({ term: null, draft: { effective_from: '', currency: terms[terms.length - 1].currency, frequency: terms[terms.length - 1].frequency, charges_mode: terms[terms.length - 1].charges_mode } })}
              data-testid="rent-term-add-button">
              <AppIcon path={mdiPlus} size="sm" className="mr-2" />{t('lease.rent_schedule.add')}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {isLoading ? null : terms.length === 0 ? (
            <div data-testid="rent-terms-empty">
              <EmptyState variant="inline" title={t('lease.rent_schedule.empty_title')} description={t('lease.rent_schedule.empty_help')} />
              {prefill && (
                <p className="mb-3 text-sm" data-testid="rent-term-prefill">
                  {t('lease.rent_schedule.prefill', {
                    rent: prefill.rent_minor !== undefined ? formatMoney(prefill.rent_minor, prefill.currency ?? data?.currency_proposal ?? null) : '—',
                    frequency: prefill.frequency ? t(`lease.rent_schedule.frequencies.${prefill.frequency}` as any) : '—',
                  })}
                </p>
              )}
              {canWrite && (
                <Button onClick={confirmInitial} data-testid="rent-term-confirm-initial-button">{t('lease.rent_schedule.confirm_initial')}</Button>
              )}
            </div>
          ) : (
            <Table data-testid="rent-terms-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t('lease.rent_schedule.effective_from')}</TableHead>
                  <TableHead className="text-right">{t('lease.rent_schedule.rent')}</TableHead>
                  <TableHead className="text-right">{t('lease.rent_schedule.charges')}</TableHead>
                  <TableHead>{t('lease.rent_schedule.frequency')}</TableHead>
                  <TableHead>{t('lease.rent_schedule.due_rule')}</TableHead>
                  <TableHead>{t('lease.rent_schedule.proration')}</TableHead>
                  <TableHead>{t('lease.rent_schedule.free_range')}</TableHead>
                  <TableHead>{t('lease.rent_schedule.source')}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {terms.map((term) => {
                  const incomplete = !term.due_rule || !term.proration_rule
                  return (
                    <TableRow key={term.uid} data-testid={`rent-term-row-${term.uid}`}>
                      <TableCell>{formatDate(term.effective_from)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatMoney(term.rent_minor, term.currency)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {term.charges_mode === 'provision' || term.charges_mode === 'flat_fee'
                          ? formatMoney(term.charges_minor ?? null, term.currency)
                          : t(`lease.rent_schedule.charges_modes.${term.charges_mode}` as any)}
                      </TableCell>
                      <TableCell>{t(`lease.rent_schedule.frequencies.${term.frequency}` as any)}</TableCell>
                      <TableCell>
                        {dueSummary(term)}
                        {incomplete && (
                          <Badge variant="warning" className="ml-1" data-testid="rent-term-incomplete-badge">{t('lease.rent_schedule.incomplete')}</Badge>
                        )}
                      </TableCell>
                      <TableCell>{term.proration_rule ? t(`lease.rent_schedule.prorations.${term.proration_rule}` as any) : null}</TableCell>
                      <TableCell>
                        {term.rent_free_from && term.rent_free_to ? `${formatDate(term.rent_free_from)} - ${formatDate(term.rent_free_to)}` : null}
                      </TableCell>
                      <TableCell>{t(`lease.rent_schedule.sources.${term.source}` as any)}</TableCell>
                      <TableCell className="text-right">
                        {canWrite && (
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => setDialog({ term })} aria-label={t('lease.rent_schedule.edit')}
                              data-testid={`rent-term-edit-button-${term.uid}`}><AppIcon path={mdiPencil} size="sm" /></Button>
                            <DeleteConfirmDialog
                              trigger={<Button variant="ghost" size="icon" aria-label={t('lease.rent_schedule.delete')}
                                data-testid={`rent-term-delete-button-${term.uid}`}><AppIcon path={mdiTrashCanOutline} size="sm" /></Button>}
                              title={t('lease.rent_schedule.delete_title')}
                              description={t('lease.rent_schedule.delete_help', { date: formatDate(term.effective_from) })}
                              isDeleting={remove.isPending}
                              onConfirm={() => remove.mutate({ params: { path: { orgCode: org, contractUid, termUid: term.uid } } })}
                            />
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ReviewTermsCard org={org} contractUid={contractUid} />

      <RentReviewsSection org={org} contractUid={contractUid} />

      {dialog && (
        <RentTermDialog
          org={org}
          contractUid={contractUid}
          term={dialog.term}
          draft={dialog.draft}
          packDefault={data?.pack_default}
          currencyProposal={data?.currency_proposal}
          isFirst={terms.length === 0}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  )
}
